import React, { useState } from "react";
import PageTitle from "components/PageTitle/PageTitle";
import { Button, CircularProgress, Grid, TextField, Dialog, DialogTitle, DialogActions, DialogContent, Checkbox, FormControlLabel, Tooltip, IconButton } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Typography } from "components/Wrappers/Wrappers";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import Field from "components/CustomFields/Field.js";
import SelectField from "components/CustomFields/SelectField.js";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";

import { dbGet, dbPost } from "utils/DBFetchers.js";
import { verifyRut } from "utils/rutValidations";
import { useHistory } from "react-router-dom";
import routes from "routes";

// styles
import useStyles from "./styles";
import { cleanEmptyKeys } from "utils/cleanEmptyKeys";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";

const blankNewPharmacyForm = {
  rut: "",
  rutVerifier: "",
  code: "",
  name: "",
  company: "",
  group_id: "",
  address: "",
  commune_id: "",
  email: "",
  phone: "",
  contact: "",
  programs: null,
};

const allPrograms = [
  { id: 1, name: "Receta solidaria" },
  { id: 2, name: "Solo mujer" },
  { id: 3, name: "Ambos" },
];

const formattedPrograms = {
  1: [1],
  2: [2],
  3: [1, 2],
};

const PROGRAM_RS_ID = 1;
const GOLAN_WALLET_STATUS = "activated-api-golan";

const newRutWalletStatusOptions = [
  { id: "activated-api-lioren", name: "Activada por Lioren" },
  { id: "activated-api-golan", name: "Activada por Golan" },
  { id: "activated-ui", name: "Activada por Web" },
];

const existingRutWalletStatusOptions = [
  ...newRutWalletStatusOptions,
  { id: "deactivated", name: "Desactivada" },
];

const getProgramNameById = (id) => {
  const program = allPrograms.find(p => p.id === Number(id));
  return program ? program.name : 'Programa Desconocido';
};

const isPharmacyNotFoundError = (error) => {
  return error?.errors?.key?.[0] === "PHARMACY_NOT_FOUND";
};

export default function ABMPharmaciesNewPage() {
  var classes = useStyles();
  const history = useHistory();
  var [isLoading, setIsLoading] = useState(false);
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const [newPharmacyForm, updateForm] = useState(blankNewPharmacyForm);
  const [, setAllPharmacies] = useState([]);
  const [isRutValid, setIsRutValid] = useState({ rut: false, message: "" });
  const [allPharmaGroups, setAllPharmaGroups] = useState([]);
  const [allCommunes, setAllCommunes] = useState([]);
  const [walletStatus, setWalletStatus] = useState("");
  const [isExistingRutWalletStatus, setIsExistingRutWalletStatus] = useState(false);
  const [walletStatusLoading, setWalletStatusLoading] = useState(false);
  const walletStatusHelperText = walletStatus
    ? isExistingRutWalletStatus
      ? "RUT existente: Modalidad configurada."
      : "RUT nuevo: seleccione la modalidad."
    : "";
  const walletStatusTooltip = (
    <>
      El estado de billetera para RUTs existentes se puede gestionar desde{" "}
      <a
        href={routes.pharmacyWalletActivationPage}
        className={classes.walletStatusTooltipLink}
      >
        Billetera Virtual - Activación/Desactivación
      </a>
      .
    </>
  );

  const [distributorNotificationOpen, setDistributorNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    rut: '',
    distributorName: '',
    pharmacyName: '',
    program: ''
  });
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [notifyGolanByMailChecked, setnotifyGolanByMailChecked] = useState(true);
  const [createdPharmacyId, setCreatedPharmacyId] = useState(null);
  const [pendingDistributorNotification, setPendingDistributorNotification] = useState(null);

  const handleChange = (event) => {
    updateForm({
      ...newPharmacyForm,
      [event.target.name]: event.target.value,
    });
  };
  const handleAutoCompleteCommuneChange = (event, newValue) => {
    updateForm({
      ...newPharmacyForm,
      commune_id: newValue ? newValue.id : null,
    });
  };

  const handleRutVerification = () => {
    var currentRut = newPharmacyForm.rut + newPharmacyForm.rutVerifier;
    var rutValid = verifyRut(currentRut);

    if (rutValid && !newPharmacyForm.rutVerifier)
      setIsRutValid({
        rut: false,
        message: "Ingrese el dígito verificador en el campo respectivo.",
      });
    else if (!rutValid) setIsRutValid({ rut: false, message: "RUT inválido." });
    else setIsRutValid({ rut: true, message: "" });
  };

  // Rut updaters
  const handleRutChange = (e) => {
    updateForm({
      ...newPharmacyForm,
      [e.target.id]: e.target.value.toUpperCase(),
    });
  };
  React.useEffect(() => {
    if (!newPharmacyForm.rut && !newPharmacyForm.rutVerifier) return; // no fetch on page load
    handleRutVerification();
  }, [newPharmacyForm.rut, newPharmacyForm.rutVerifier]);

  /* Fills some field name options */
  const getPharmaGroups = () => {
    dbGet("pharmagroups").then((data) => {
      data = data.sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        // a must be equal to b
        return 0;
      });
      let mapped = data.map((item) => {
        return { id: item["id"], name: item["name"] };
      });
      setAllPharmaGroups(mapped);
    });
  };
  const getCommunes = () => {
    dbGet("communes").then((data) => {
      data = data.sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        // a must be equal to b
        return 0;
      });
      let mapped = data.map((item) => {
        return { id: item["id"], name: item["name"] };
      });
      setAllCommunes(mapped);
    });
  };
  const getPharmacies = () => {
    dbGet("pharmacies").then((data) => {
      let mapped = data.map((item) => {
        return {
          id: item["id"],
          name: item["name"],
          rut: item["rut"],
        };
      });
      setAllPharmacies(mapped);
    });
  };

  /* Basic form check */
  const checkFormComplete = () => {
    if (
      !newPharmacyForm.name ||
      !newPharmacyForm.rut ||
      !newPharmacyForm.code ||
      !newPharmacyForm.programs ||
      !isRutValid.rut ||
      !walletStatus ||
      walletStatusLoading
    )
      return false;
    return true;
  };

  const getCreatedPharmacyId = (data) => {
    return data?.id || data?.pharmacy_id || data?.pharmacy?.id || null;
  };

  const notifyGolanByMail = async (pharmacyId) => {
    if (!pharmacyId) {
      snackbarOpen("error", "No se pudo notificar a Golan: falta el ID de la farmacia creada.");
      return false;
    }

    try {
      await dbPost(`wallet/golan/pharmacies/${pharmacyId}/notifications`, {});
      snackbarOpen("success", "Notificación enviada a Golan por mail");
      return true;
    } catch (error) {
      const errorData = error?.errors || error?.response?.data?.errors || error;
      const backendMessage =
        (Array.isArray(errorData?.description_es) ? errorData.description_es[0] : errorData?.description_es) ||
        (Array.isArray(errorData?.description) ? errorData.description[0] : errorData?.description) ||
        error?.response?.data?.description_es ||
        error?.message ||
        "Error al enviar correo";

      snackbarOpen("error", backendMessage);
      return false;
    }
  };

  const getDistributorNotificationMessage = (data) => {
    if (!data?.existent_distributor_name) {
      return null;
    }

    return {
      rut: data?.rut || `${newPharmacyForm.rut}${newPharmacyForm.rutVerifier}`,
      distributorName: data.existent_distributor_name,
      pharmacyName: newPharmacyForm.name,
      program: getProgramNameById(newPharmacyForm.programs),
    };
  };

  const finishSuccessfulSubmit = () => {
    updateForm(blankNewPharmacyForm);
    setCreatedPharmacyId(null);
    setPendingDistributorNotification(null);
    setIsLoading(false);
    history.push(routes.abmPharmacies);
  };

  const showDistributorNotification = (message) => {
    setNotificationMessage(message);
    setDistributorNotificationOpen(true);
    setIsLoading(false);
  };

  const continueAfterGolanDialog = () => {
    setSuccessDialogOpen(false);

    if (pendingDistributorNotification) {
      showDistributorNotification(pendingDistributorNotification);
      return;
    }

    finishSuccessfulSubmit();
  };

  const handleSuccessDialogAccept = async () => {
    if (notifyGolanByMailChecked && walletStatus === GOLAN_WALLET_STATUS) {
      const notificationSent = await notifyGolanByMail(createdPharmacyId);
      if (!notificationSent) {
        return;
      }
    }

    continueAfterGolanDialog();
  };

  const handleSuccessDialogClose = () => {
    continueAfterGolanDialog();
  };

  /* Submit handling */
  const handleSubmit = async () => {
    if (!checkFormComplete()) {
      snackbarOpen(
        "error",
        "Hay campos sin completar o incorrectos. Por favor completelos para poder añadir la farmacia.",
      );
      return;
    }
    setIsLoading(true);

    // Give adecuate format to send form to db
    let completedForm = {
      rut: newPharmacyForm.rut + newPharmacyForm.rutVerifier,
      code: newPharmacyForm.code,
      name: newPharmacyForm.name,
      company: newPharmacyForm.company,
      group_id: newPharmacyForm.group_id,
      address: newPharmacyForm.address,
      commune_id: newPharmacyForm.commune_id,
      email: newPharmacyForm.email,
      phone: newPharmacyForm.phone,
      contact: newPharmacyForm.contact,
    };

    completedForm = cleanEmptyKeys(completedForm);
    completedForm["programs"] = formattedPrograms[newPharmacyForm.programs];
    if (completedForm["programs"].includes(PROGRAM_RS_ID)) {
      completedForm["wallet_status"] = walletStatus;
    }

    // Sends to db


    try {
      const data = await dbPost(`pharmacies`, completedForm);
      snackbarOpen("success", "Farmacia añadida exitosamente");
      const distributorNotification = getDistributorNotificationMessage(data);

      if (walletStatus === GOLAN_WALLET_STATUS) {
        setCreatedPharmacyId(getCreatedPharmacyId(data));
        setPendingDistributorNotification(distributorNotification);
        setnotifyGolanByMailChecked(true);
        setSuccessDialogOpen(true);
        setIsLoading(false);
        return;
      }

      if (distributorNotification) {
        showDistributorNotification(distributorNotification);
        return;
      }

      finishSuccessfulSubmit();
    } catch (error) {
      snackbarOpen(
        "error",
        error[0] ? error[0].description_es : "Error al añadir farmacia",
      );
      setIsLoading(false);
      return;
    }
  };

  /* Cancel editing pharmacy and go one page backwards */
  const cancelAndGoBack = () => {
    updateForm(blankNewPharmacyForm);
    history.goBack();
  };

  const handleNotificationCloseAndRedirect = () => {
    setDistributorNotificationOpen(false);
    finishSuccessfulSubmit();
  };

  /* On page load, get all data */
  React.useEffect(() => {
    getPharmacies();
    getPharmaGroups();
    getCommunes();
  }, []);

  const title = `Nueva Farmacia${newPharmacyForm.name ? ": " : ""}${
    newPharmacyForm.name ? newPharmacyForm.name : ""
  }`;

  React.useEffect(() => {
    const currentRut = newPharmacyForm.rut + newPharmacyForm.rutVerifier;
    const rutReady =
      newPharmacyForm.rut &&
      newPharmacyForm.rutVerifier &&
      verifyRut(currentRut);

    if (!rutReady) {
      setWalletStatus("");
      setIsExistingRutWalletStatus(false);
      setWalletStatusLoading(false);
      return;
    }

    setWalletStatusLoading(true);
    let ignoreResponse = false;

    dbGet(`wallet/internal/status/pharmacies/${currentRut}`)
      .then((data) => {
        if (ignoreResponse) return;
        setWalletStatus(data.wallet_status || "");
        setIsExistingRutWalletStatus(true);
      })
      .catch((error) => {
        if (ignoreResponse) return;
        if (isPharmacyNotFoundError(error)) {
          setWalletStatus("activated-ui");
          setIsExistingRutWalletStatus(false);
          return;
        }

        setWalletStatus("");
        setIsExistingRutWalletStatus(false);
        snackbarOpen("error", "No se pudo obtener el estado de billetera para el RUT ingresado.");
      })
      .finally(() => {
        if (ignoreResponse) return;
        setWalletStatusLoading(false);
      });

    return () => {
      ignoreResponse = true;
    };
  }, [newPharmacyForm.rut, newPharmacyForm.rutVerifier]);

  return (
    <>
      <Dialog open={successDialogOpen} onClose={handleSuccessDialogClose}>
        <DialogTitle style={{ textAlign: "left" }}>
          {"Nueva asociación con Golan registrada"}
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={notifyGolanByMailChecked}
                onChange={(event) => setnotifyGolanByMailChecked(event.target.checked)}
                color="primary"
              />
            }
            label="Enviar notificación por correo."
          />
        </DialogContent>
        <DialogActions className={classes.popupDivCenter}>
          <Button
            onClick={handleSuccessDialogAccept}
            color="primary"
            variant="contained"
            className={classes.popupButtonSpacing}
            autoFocus
          >
            {notifyGolanByMailChecked ? "Enviar" : "Cerrar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={distributorNotificationOpen} onClose={handleNotificationCloseAndRedirect}>
        <DialogTitle>{"Asociación Automática de Distribuidor"}</DialogTitle>
        <div style={{ padding: '0 24px 10px 24px' }}>
          <Typography>
            {`El RUT `}
            <strong style={{ color: '#000000' }}>{notificationMessage.rut}</strong>
            {` se encuentra asociado al distribuidor `}
            <strong style={{ color: '#078ecf' }}>{notificationMessage.distributorName}</strong>
            {` en el programa ${notificationMessage.program}.`}
          </Typography>
          <br />
          <Typography>
            {`Por lo tanto, la farmacia `}
            <strong style={{ color: '#000000' }}>{notificationMessage.pharmacyName}</strong>
            {` se asoció automáticamente a dicho distribuidor.`}
          </Typography>
        </div>
        <DialogActions className={classes.popupDivCenter}>
          <Button
            onClick={handleNotificationCloseAndRedirect}
            color="primary"
            variant="contained"
            className={classes.popupButtonSpacing}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <div>
        <PageTitle
          title={title}
        />
      </div>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <Grid
        style={{ display: "flex", margin: 0 }}
        container
        direction="row"
        justify="flex-start"
        alignItems="center"
        spacing={4}
      >
        <form autoComplete="off">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" className={classes.text}>
                Datos de la farmacia
              </Typography>
              <div
                className={classes.text}
                style={{
                  marginTop: "1%",
                  display: "flex",
                  justifyContent: "space-between",
                  width: 700,
                }}
              >
                <Typography weight="medium">
                  A continuación ingrese los datos requeridos para añadir una
                  nueva farmacia.
                </Typography>
              </div>
              <div style={{ marginTop: "4%" }}>
                <Field
                  labelName="Nombre *"
                  required={true}
                  id="name"
                  value={newPharmacyForm.name}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <Field
                  labelName="Código de farmacia *"
                  required={true}
                  id="code"
                  value={newPharmacyForm.code}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <div className={classes.walletRutStatusRow}>
                  <div className={classes.walletRutBlock}>
                    <Typography className={classes.text} weight="bold">
                      RUT * (sin guión)
                    </Typography>
                    <div className={classes.walletRutInputs}>
                      <TextField
                        required={true}
                        id="rut"
                        name="rut"
                        className={classes.walletRutField}
                        variant="outlined"
                        margin="dense"
                        helperText={isRutValid.message}
                        onChange={handleRutChange}
                        InputLabelProps={{ shrink: true }}
                        value={newPharmacyForm.rut}
                        error={!isRutValid.rut}
                        placeholder="RUT"
                      />
                      <TextField
                        required={true}
                        id="rutVerifier"
                        name="rutVerifier"
                        className={classes.walletRutVerifierField}
                        variant="outlined"
                        margin="dense"
                        onChange={handleRutChange}
                        InputLabelProps={{ shrink: true }}
                        value={newPharmacyForm.rutVerifier}
                        error={!isRutValid.rut}
                        placeholder="DV"
                      />
                    </div>
                  </div>
                  <div className={classes.walletStatusBlock}>
                    <div className={classes.walletStatusLabelRow}>
                      <Typography weight="bold">
                        Billetera
                      </Typography>
                      <Tooltip title={walletStatusTooltip} interactive>
                        <IconButton
                          className={classes.walletStatusInfoButton}
                          size="small"
                          aria-label="Información sobre estado de billetera"
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <div className={classes.walletStatusControlRow}>
                      <SelectField
                        divClassName={classes.walletStatusSelect}
                        fieldClassName={classes.walletStatusField}
                        id="walletStatus"
                        label={walletStatusLoading ? "Consultando..." : "Estado del RUT"}
                        name="walletStatus"
                        value={walletStatus}
                        helperText={walletStatusHelperText}
                        disabled={
                          !walletStatus ||
                          isExistingRutWalletStatus ||
                          walletStatusLoading
                        }
                        onChange={(event) => setWalletStatus(event.target.value)}
                        allValues={
                          isExistingRutWalletStatus
                            ? existingRutWalletStatusOptions
                          : newRutWalletStatusOptions
                        }
                      />
                    </div>
                  </div>
                </div>
                <SelectField
                  divClassName={classes.generalPurposeItems}
                  fieldClassName={classes.generalPurposeField}
                  displayText="Programa/s *"
                  required={true}
                  id="programs"
                  label="Seleccione"
                  name="programs"
                  value={newPharmacyForm.programs}
                  onChange={handleChange}
                  allValues={allPrograms ? allPrograms : []}
                />
                <Field
                  labelName="Compañia/Empresa"
                  required={true}
                  id="company"
                  value={newPharmacyForm.company}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <Field
                  labelName="Domicilio"
                  required={true}
                  id="address"
                  value={newPharmacyForm.address}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <div className={classes.generalPurposeItems}>
                  <Typography className={classes.text} weight="bold">
                    Comuna
                  </Typography>
                  <Autocomplete
                    freeSolo
                    onChange={handleAutoCompleteCommuneChange}
                    options={allCommunes ? allCommunes : []}
                    getOptionLabel={(option) =>
                      option.name ? option.name : ""
                    }
                    className={classes.generalPurposeField}
                    renderInput={(params) => (
                      <TextField
                        className={classes.selectionTextEnabled}
                        {...params}
                        placeholder="Seleccione"
                        margin="dense"
                        variant="outlined"
                      />
                    )}
                  />
                </div>
                <Field
                  labelName="Teléfono"
                  required={true}
                  id="phone"
                  value={newPharmacyForm.phone}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <Field
                  labelName="Email"
                  required={true}
                  id="email"
                  value={newPharmacyForm.email}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <Field
                  labelName="Contacto"
                  required={true}
                  id="contact"
                  value={newPharmacyForm.contact}
                  divClassType={classes.generalPurposeItems}
                  fieldClassType={classes.generalPurposeField}
                  onChange={handleChange}
                ></Field>
                <SelectField
                  divClassName={classes.generalPurposeItems}
                  fieldClassName={classes.generalPurposeField}
                  displayText="Grupo"
                  id="group_id"
                  label="Seleccione"
                  name="group_id"
                  value={newPharmacyForm.group_id}
                  onChange={handleChange}
                  allValues={allPharmaGroups ? allPharmaGroups : []}
                />
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={10}>
            <Grid item xs={12}>
              <div className={classes.buttonSpacing}>
                {isLoading ? (
                  <CircularProgress size={26} className={classes.loginLoader} />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                  >
                    Guardar
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={cancelAndGoBack}
                >
                  Cancelar
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </>
  );
}
